import { Card, CardContent, Grid, Typography } from '@material-ui/core';
import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import {
  ExhibitDto,
  exhibitInit,
  exhibitValidationSchema,
} from '../../exhibits.dtos';
import CardContainer from '../../../../common/components/cards/cardContainer';
import ExhibitInfo from './exhibitInfo';
import ExhibitQRCode from './exhibitQRCode';
import ExhibitImage from './exhibitImage';
import ExhibitContent from './exhibitContent';
import { getType } from 'typesafe-actions';
import {
  createExhibitActions,
  deleteThumbnailAction,
  exhibitActions,
  updateExhibitActions,
} from '../../exhibits.actions';
import { rootSelector } from '../../../../common/selectors/selectors';
import { useDispatch } from 'react-redux';
import BottomActionFormBar from '../../../../common/components/buttons/bottomActionFormBar';
import AttachmentDropzone from '../../../../common/components/dialog/AttachmentDropzone';
import DeleteDialog from '../../../../common/components/dialog/DeleteDialog';
import { deleteThumbnailApi } from '../../exhibits.api';

interface DetailParams {
  id: string;
}

const ExhibitForm: React.FC<RouteComponentProps<DetailParams>> = ({
  match,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const systemExhibit = rootSelector((state) => state.exhibits.exhibit);

  const [dto, setDto] = useState<ExhibitDto>(exhibitInit);
  const [fileUploads, setFileUploads] = useState<File[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedImageDelete, setSelectedImageDelete] = useState<string>('');

  useEffect(() => {
    if (match.params.id !== undefined) {
      dispatch({
        type: getType(exhibitActions.request),
        payload: match.params.id,
      });
    }
  }, [match.params.id, dispatch]);

  useEffect(() => {
    if (match.params.id !== undefined) {
      setDto(systemExhibit);
    } else {
      setDto(exhibitInit);
    }
  }, [systemExhibit, dispatch, match.params.id]);

  const handleCancel = () => {
    history.push('/exhibits');
  };

  const onAttachmentDelete = (file: File) =>
    setFileUploads(
      fileUploads.filter((attachment) => attachment.name !== file.name)
    );
  const onAttachmentUpload = (files: File[]) => {
    setFileUploads([...fileUploads, ...files]);
  };

  const openDeleteDialog = (fileName: string) => {
    setSelectedImageDelete(fileName);
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
  };

  const handleDialogDelete = async (fileName: string) => {
    await deleteThumbnailAction(dto.id!, fileName);

    dispatch({
      type: getType(exhibitActions.request),
      payload: match.params.id,
    });

    setDeleteDialogOpen(false);
  };

  function onKeyDown(keyEvent: any) {
    if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
      keyEvent.preventDefault();
    }
  }

  return (
    <>
      {deleteDialogOpen ? (
        <DeleteDialog
          open={deleteDialogOpen}
          onDelete={handleDialogDelete}
          onClose={handleDeleteDialogClose}
          value={selectedImageDelete}
        />
      ) : null}
      <Formik
        initialValues={dto}
        validationSchema={exhibitValidationSchema}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={(values, { setSubmitting, resetForm }) => {
          setTimeout(() => {
            setSubmitting(false);
          }, 700);

          let allNew = values.id === '';

          dispatch({
            type: allNew
              ? getType(createExhibitActions.request)
              : getType(updateExhibitActions.request),
            payload: { ...values, fileUpload: fileUploads },
          });

          // handleCancel();
        }}
        enableReinitialize={true}
      >
        {(props) => {
          const {
            values,
            errors,
            isSubmitting,
            touched,
            setFieldValue,
            handleBlur,
            handleChange,
            handleSubmit,
            setErrors,
          } = props;
          return (
            <form onSubmit={handleSubmit} noValidate onKeyDown={onKeyDown}>
              <CardContainer
                title='Add/Edit Exhibit'
                cards={
                  <>
                    <Grid container item spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant='subtitle1'>
                          Exhibit Details
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <ExhibitInfo
                          values={values}
                          errors={errors}
                          touched={touched}
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          setFieldValue={setFieldValue}
                          setErrors={setErrors}
                        />
                      </Grid>
                      <Grid container item xs={8} spacing={2}>
                        {values.id !== '' ? (
                          <Grid item sm={4}>
                            <ExhibitQRCode
                              qrCode={values.qrCode!}
                              exhibitName={values.name}
                            />
                          </Grid>
                        ) : null}

                        <Grid item sm={5}>
                          <Card>
                            <CardContent>
                              <Grid item xs={12}>
                                <Typography variant='subtitle2'>
                                  Exhibit Thumbnail
                                </Typography>
                              </Grid>
                              <Grid
                                container
                                item
                                justify='center'
                                direction='column'
                                alignItems='center'
                              >
                                {values.thumbnail !== '' &&
                                values.thumbnail !== undefined ? (
                                  <ExhibitImage
                                    fileName={values.thumbnail}
                                    openDeleteDialog={openDeleteDialog}
                                  />
                                ) : (
                                  <AttachmentDropzone
                                    attachments={fileUploads}
                                    onDelete={onAttachmentDelete}
                                    onDrop={onAttachmentUpload}
                                    maxFiles={1}
                                  />
                                )}
                              </Grid>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12} style={{ marginBottom: '50px' }}>
                      <ExhibitContent
                        content={values.content}
                        onChange={setFieldValue}
                      />
                    </Grid>
                    <BottomActionFormBar
                      cancelClickHandler={handleCancel}
                      submitting={isSubmitting}
                    />
                  </>
                }
              />
            </form>
          );
        }}
      </Formik>
    </>
  );
};

export default ExhibitForm;
